/**
 * Identidade da aba do navegador.
 *
 * O backend usa esse valor (header `X-Tab-Id`) para escopar a sessão de programa — role e
 * organização ativa — por aba, em vez de por conta. Sem isso, trocar de organização em uma aba
 * altera o contexto de todas as outras, o que fazia ativos de um CNPJ irem parar no carrinho
 * de outro.
 *
 * `sessionStorage` é o armazenamento certo aqui: é isolado por aba (ao contrário de
 * `localStorage`, que é compartilhado) e sobrevive ao refresh, morrendo junto com a aba.
 *
 * Ressalva: "Duplicar guia", "Reabrir guia fechada" e a restauração de sessão do navegador
 * copiam o `sessionStorage`, então a aba nova nasceria com o id da original. O
 * `BroadcastChannel` abaixo detecta essa colisão no boot e faz a aba recém-aberta regenerar
 * o próprio id.
 *
 * O tabId não tem valor de autorização: é apenas um discriminador de escopo. Quem decide quais
 * organizações a conta pode assumir continua sendo o backend, em POST /programs/roles/assume.
 */

const TAB_ID_KEY = 'x-tab-id'
const CHANNEL_NAME = 'mutant-portal-tabs'
const CLAIM = 'claim'
const CONFLICT = 'conflict'

let tabId = null
let channel = null

function generate() {
  return (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function read() {
  try {
    return sessionStorage.getItem(TAB_ID_KEY)
  } catch (e) {
    // navegação privada ou storage bloqueado
    return null
  }
}

function persist(id) {
  tabId = id
  try {
    sessionStorage.setItem(TAB_ID_KEY, id)
  } catch (e) {
    // sem persistência o id vale só enquanto a página estiver carregada,
    // o que ainda é melhor do que não ter escopo nenhum
  }
}

export function getTabId() {
  if (!tabId) {
    const stored = read()

    if (stored) {
      tabId = stored
    } else {
      persist(generate())
    }
  }

  return tabId
}

/**
 * Anuncia o id desta aba e resolve colisões com abas duplicadas.
 *
 * Quem acabou de abrir é quem cede: a aba original responde CONFLICT e mantém seu id, a
 * recém-aberta gera outro. O BroadcastChannel não entrega mensagens ao próprio emissor, então
 * só abas realmente distintas participam da negociação.
 */
export function initTabIdentity() {
  const id = getTabId()

  if (typeof BroadcastChannel === 'undefined') {
    return id
  }

  try {
    channel = new BroadcastChannel(CHANNEL_NAME)
  } catch (e) {
    return id
  }

  channel.onmessage = ({ data }) => {
    if (!data || data.tabId !== tabId) {
      return
    }

    if (data.type === CLAIM) {
      // outra aba está reivindicando o nosso id: ela é a duplicata
      channel.postMessage({ type: CONFLICT, tabId })
      return
    }

    if (data.type === CONFLICT) {
      persist(generate())
      channel.postMessage({ type: CLAIM, tabId })
    }
  }

  channel.postMessage({ type: CLAIM, tabId: id })

  // o id pode ter sido regenerado durante a negociação acima
  return getTabId()
}

export default getTabId
